import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { X, UserPlus } from 'lucide-react';
import { useCustomerAuth } from '../context/CustomerAuthContext';

// Routes where this banner would block actual conversions or feel intrusive
const SUPPRESS_ON_ROUTES = ['/shrimp-room', '/checkout', '/cart', '/customer-login', '/customer-register'];

export const FirstOrderBanner = () => {
  const { customer } = useCustomerAuth();
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  const suppressed = SUPPRESS_ON_ROUTES.some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (customer || suppressed) return;
    if (sessionStorage.getItem('dismissed_first_order')) return;

    let scrolled = false;
    let elapsed = false;

    const tryShow = () => {
      if (scrolled && elapsed) setVisible(true);
    };

    const handleScroll = () => {
      if (window.scrollY > 400) {
        scrolled = true;
        tryShow();
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });

    const timer = setTimeout(() => {
      elapsed = true;
      tryShow();
    }, 20000);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, [customer, suppressed]);

  if (!visible || customer || suppressed) return null;

  const dismiss = () => {
    setVisible(false);
    sessionStorage.setItem('dismissed_first_order', '1');
  };

  return (
    <div
      data-testid="first-order-banner"
      className="hidden sm:block fixed bottom-5 right-5 z-50 w-80 animate-in slide-in-from-bottom-5 fade-in duration-500"
    >
      <div className="relative bg-surface border border-divider rounded-xl shadow-xl overflow-hidden">
        <div className="h-1 bg-shrimp-green" />
        <button
          onClick={dismiss}
          data-testid="dismiss-first-order-banner"
          className="absolute top-2.5 right-2.5 p-1 rounded-full hover:bg-surface-hover transition-colors text-subtle"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-4 pt-3">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-full bg-shrimp-green/10 flex items-center justify-center">
              <UserPlus className="w-4.5 h-4.5 text-shrimp-green" />
            </div>
            <div>
              <p className="font-bold text-heading text-sm leading-tight">
                Get 5% off your first order
              </p>
              <p className="text-label text-xs mt-1 leading-relaxed">
                Create a free account and enjoy an exclusive discount on your first purchase.
              </p>
            </div>
          </div>
          <Link
            to="/customer-login"
            onClick={dismiss}
            data-testid="first-order-register-link"
            className="mt-3 block w-full text-center text-sm font-semibold py-2 rounded-lg bg-shrimp-green text-white hover:brightness-110 transition-all"
          >
            Register now
          </Link>
        </div>
      </div>
    </div>
  );
};
